/**
 * Wallet validation service for validating cryptocurrency wallet addresses
 */

/**
 * Validates a BNB/BNB Smart Chain wallet address
 * @param {string} address - Wallet address to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateBnbWalletAddress(address) {
  if (!address) return false;
  
  // Basic format check: must start with 0x and be followed by 40 hex characters
  const basicFormat = /^0x[a-fA-F0-9]{40}$/;
  if (!basicFormat.test(address)) {
    return false;
  }
  
  // For BNB validation, we'll be more lenient and just check the format
  // EIP-55 checksum validation can be skipped for broader compatibility
  return true;
}

/**
 * Validates Ethereum address checksum according to EIP-55
 * @param {string} address - Address to validate
 * @returns {boolean} True if checksum is valid or if all lowercase/uppercase, false otherwise
 */
export function validateEthereumAddressChecksum(address) {
  if (!address) return false;
  
  // If it's all lowercase or all uppercase, it's valid (no checksum)
  if (address === address.toLowerCase() || address === address.toUpperCase()) {
    return true;
  }
  
  // Apply EIP-55 checksum validation
  const addressHash = keccak256(address.toLowerCase());
  for (let i = 0; i < 40; i++) {
    // The nth letter should be uppercase if the nth digit of the hash is 8 or higher
    if (parseInt(addressHash[i], 16) >= 8 && address[i].toUpperCase() !== address[i]) {
      return false;
    }
    if (parseInt(addressHash[i], 16) < 8 && address[i].toLowerCase() !== address[i]) {
      return false;
    }
  }
  
  return true;
}

/**
 * Helper function to calculate keccak256 hash (simplified for EIP-55)
 * Note: In a real implementation, you'd want to use a proper hashing library
 */
function keccak256(str) {
  // This is a simplified placeholder - in a real app you'd use a proper implementation
  // For the purpose of checksum validation, we'll return the hex representation of string hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  
  // Convert hash to hex string
  let hex = Math.abs(hash).toString(16);
  // Pad with zeros to make it 40 characters (like Ethereum addresses)
  while (hex.length < 40) {
    hex = "0" + hex;
  }
  
  return hex;
}

/**
 * Validates various types of wallet addresses
 * @param {string} address - Wallet address to validate
 * @param {string} blockchain - Blockchain type ('BNB', 'ETH', 'BTC', etc.)
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateWalletAddress(address, blockchain = 'BNB') {
  if (!address) {
    return {
      isValid: false,
      error: 'Wallet address is required'
    };
  }
  
  switch (blockchain) {
    case 'BNB':
    case 'ETH':
      if (!validateBnbWalletAddress(address)) {
        return {
          isValid: false,
          error: 'Please enter a valid BNB Smart Chain wallet address (0x followed by 40 hex characters)'
        };
      }
      break;
      
    case 'BTC':
      if (!validateBitcoinAddress(address)) {
        return {
          isValid: false,
          error: 'Please enter a valid Bitcoin address'
        };
      }
      break;
      
    default:
      // Default to BNB/ETH validation for compatibility
      if (!validateBnbWalletAddress(address)) {
        return {
          isValid: false,
          error: 'Please enter a valid wallet address'
        };
      }
  }
  
  return {
    isValid: true,
    error: null
  };
}

/**
 * Validates a Bitcoin address (simplified)
 * @param {string} address - Bitcoin address to validate
 * @returns {boolean} True if valid, false otherwise
 */
function validateBitcoinAddress(address) {
  // Basic validation for Bitcoin addresses (P2PKH, P2SH)
  // This is a simplified check - full validation would require more complex logic
  const patterns = [
    /^[13][a-km-zA-HJ-NP-Z0-9]{25,34}$/, // P2PKH and P2SH addresses
    /^bc1[ac-hj-np-z02-9]{39,59}$/i      // Bech32 (SegWit) addresses
  ];
  
  return patterns.some(pattern => pattern.test(address));
}

/**
 * Sanitizes a wallet address by removing whitespace and validating format
 * @param {string} address - Raw wallet address input
 * @returns {string} Sanitized address or empty string if invalid
 */
export function sanitizeWalletAddress(address) {
  if (!address) return '';
  
  // Remove any whitespace and other non-hex characters (except 0x prefix)
  const cleanAddress = address.trim();
  
  // Basic validation of format
  if (!cleanAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
    return '';
  }
  
  return cleanAddress;
}

/**
 * Creates a rate limiter for API endpoints
 */
export class RateLimiter {
  constructor(options = {}) {
    this.maxRequests = options.maxRequests || 10;      // Max requests per window
    this.windowMs = options.windowMs || 15 * 60 * 1000; // 15 minutes window
    this.clients = new Map(); // Store request counts per client
  }

  /**
   * Check if a client has exceeded the rate limit
   * @param {string} clientId - Unique identifier for the client (IP address)
   * @returns {Object} Result with isBlocked and timeRemaining
   */
  checkLimit(clientId) {
    const now = Date.now();
    let client = this.clients.get(clientId);
    
    if (!client) {
      // First request from this client
      this.clients.set(clientId, {
        firstRequest: now,
        lastRequest: now,
        count: 1
      });
      return { isBlocked: false, timeRemaining: 0 };
    }
    
    // Clean up old records (outside the window)
    if (now - client.firstRequest > this.windowMs) {
      this.clients.delete(clientId);
      this.clients.set(clientId, {
        firstRequest: now,
        lastRequest: now,
        count: 1
      });
      return { isBlocked: false, timeRemaining: 0 };
    }
    
    // Update last request time
    client.lastRequest = now;
    client.count++;
    
    if (client.count > this.maxRequests) {
      const timeRemaining = this.windowMs - (now - client.firstRequest);
      return { 
        isBlocked: true, 
        timeRemaining: Math.ceil(timeRemaining / 1000) // in seconds
      };
    }
    
    return { isBlocked: false, timeRemaining: 0 };
  }

  /**
   * Clean up old records periodically to prevent memory leaks
   */
  cleanup() {
    const now = Date.now();
    for (const [clientId, client] of this.clients) {
      if (now - client.firstRequest > this.windowMs) {
        this.clients.delete(clientId);
      }
    }
  }
}

// Export default object
export default {
  validateBnbWalletAddress,
  validateEthereumAddressChecksum,
  validateWalletAddress,
  sanitizeWalletAddress,
  RateLimiter
};